import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy, Users, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Cinematic live-display page for the wedding reception. Open this on a
 * laptop and project to a TV/screen. Connects via WebSocket and animates
 * guest joins + correct votes in real-time.
 *
 *   /reception/{game_code}
 */
export default function ReceptionScreen() {
  const { code } = useParams();
  const [game, setGame] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [feed, setFeed] = useState([]); // last 6 events
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!code) return;
    const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/reception/${code.toUpperCase()}`;
    let alive = true;

    function open() {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { if (alive) setConnected(true); };
      ws.onclose = () => {
        if (!alive) return;
        setConnected(false);
        setTimeout(open, 2000);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* noop */ } };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "snapshot") {
            setGame(m.game);
            setLeaderboard(m.leaderboard || []);
          } else if (m.type === "guest_joined") {
            setFeed((f) => [{ id: Date.now() + Math.random(), text: `${m.guest_name} joined`, kind: "join" }, ...f].slice(0, 6));
          } else if (m.type === "vote") {
            setFeed((f) => [{
              id: Date.now() + Math.random(),
              text: m.is_correct ? `${m.guest_name} nailed it (+${m.score_delta})` : `${m.guest_name} guessed`,
              kind: m.is_correct ? "win" : "guess",
            }, ...f].slice(0, 6));
          }
        } catch (e) { /* noop */ }
      };
    }
    open();

    // Keep-alive ping every 25s so reverse-proxies don't kill us
    const ping = setInterval(() => {
      try { wsRef.current?.send("ping"); } catch (e) { /* noop */ }
    }, 25000);

    return () => {
      alive = false;
      clearInterval(ping);
      try { wsRef.current?.close(); } catch (e) { /* noop */ }
    };
  }, [code]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c0c0e] via-[#1a1310] to-black text-white relative overflow-hidden" data-testid="reception-screen">
      {/* Decorative orbs */}
      <div className="absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-[#C8A96B]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#C8A96B]/10 blur-3xl pointer-events-none" />

      <div className="relative px-12 py-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-12">
          <div>
            <p className="text-[12px] uppercase tracking-[0.32em] text-[var(--gold)] mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Locked In · Live
            </p>
            <h1 className="font-serif text-6xl md:text-8xl font-light leading-none">
              {game?.couple_names || "Loading…"}
            </h1>
            <p className="text-white/60 text-2xl mt-3 font-script">share the moment</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/50">Game Code</p>
            <p className="font-mono text-7xl text-[var(--gold)] tracking-widest mt-1">{code?.toUpperCase()}</p>
            <p className="text-xs text-white/50 mt-3">
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${connected ? "bg-green-400 animate-pulse" : "bg-red-500"}`} />
              {connected ? "Live" : "Reconnecting…"}
            </p>
          </div>
        </div>

        {/* Body grid */}
        <div className="grid lg:grid-cols-12 gap-10 flex-1">
          {/* Leaderboard */}
          <div className="lg:col-span-7" data-testid="leaderboard">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--gold)] mb-5 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Top Guests
            </p>
            {leaderboard.length === 0 ? (
              <p className="text-white/40 text-2xl font-light">Waiting for guests to join…</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.slice(0, 8).map((g, i) => (
                  <div
                    key={g.name + i}
                    className={`flex items-center gap-5 px-6 py-4 rounded-2xl border ${
                      i === 0 ? "bg-[#C8A96B]/15 border-[#C8A96B]/40" : "bg-white/5 border-white/10"
                    }`}
                  >
                    <span className={`font-serif text-3xl w-10 text-center ${i === 0 ? "text-[var(--gold)]" : "text-white/50"}`}>{i + 1}</span>
                    <span className="font-serif text-3xl flex-1 truncate">{g.name}</span>
                    <span className={`font-mono text-3xl ${i === 0 ? "text-[var(--gold)]" : "text-white/80"}`}>{g.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live feed */}
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--gold)] mb-5 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Live Feed
            </p>
            <div className="space-y-2 min-h-[240px]" data-testid="live-feed">
              {feed.length === 0 ? (
                <p className="text-white/40 text-lg italic">Watch this space…</p>
              ) : (
                feed.map((e) => (
                  <div
                    key={e.id}
                    className={`px-5 py-3 rounded-xl border animate-in fade-in slide-in-from-right-4 duration-500 ${
                      e.kind === "win" ? "bg-[#C8A96B]/20 border-[#C8A96B]/40 text-[var(--gold)]"
                      : e.kind === "join" ? "bg-blue-500/10 border-blue-500/30 text-blue-300"
                      : "bg-white/5 border-white/10 text-white/70"
                    }`}
                  >
                    <p className="font-serif text-xl">{e.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-auto pt-8 border-t border-white/10">
          <p className="text-white/40 text-sm">Join at <span className="text-[var(--gold)] font-mono text-xl tracking-widest">lockedinforever.com/play/{code?.toUpperCase()}</span></p>
        </div>
      </div>
    </div>
  );
}
